import { useEffect, useRef, useState } from 'react';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { sdk } from 'src/utilities/sdk';
import { IError } from 'src/utilities/shared-types';

export enum AsyncTaskStatus {
  IDLE,
  IN_PROGRESS,
  COMPLETE,
  ERROR,
}

export enum AsyncTaskAPIStatusCodes {
  IN_PROGRESS = 202,
  COMPLETED_SUCCESSFULLY = 204,
  COMPLETED_UNSUCCESSFULLY = 422,
}

export interface IAsyncTaskResponse {
  errors?: IError[];
  task_id?: string;
  status?: string;
  task_type?: string;
  uri?: string;
  id?: string;
  download_url?: string;
  __status__: AsyncTaskAPIStatusCodes;
}

export interface IAsyncTaskStatus {
  errors?: string[];
  status: AsyncTaskStatus;
  task_id?: string;
  task_type?: string;
  downloadUrl?: string;
}

type PollingResponseTransformer = (response) => IAsyncTaskResponse;

interface IAsyncTaskConfig {
  pollingResponseTransformer?: PollingResponseTransformer;
}

const defaultPollingResponseTransformer = (response) => response;
export interface IUseAsyncTask extends IAsyncTaskStatus {
  startAsyncTask: (
    asyncTaskFunction: () => Promise<IAsyncTaskResponse>,
    config?: IAsyncTaskConfig
  ) => Promise<void>;
}

export const POLLING_INTERVAL = 200;

/**
 * IMPORTANT:
 * While this hook can be used independently, we recommend using useAsyncTaskHandler
 * which provides additional functionality like onSuccess and onError callbacks.
 */

/**
 * @deprecated This function has been deprecated. Please use useAsyncTaskHandler instead.
 */
export function useAsyncTask(): IUseAsyncTask {
  const [taskStatus, setTaskStatus] = useState<IAsyncTaskStatus>({
    status: AsyncTaskStatus.IDLE,
    errors: [],
  });
  const pollingInterval = useRef(null);
  const hasRequestInFlight = useRef(false);
  const taskId = useRef(null);
  const taskType = useRef(null);

  useEffect(() => {
    return stopPollingInterval;
  }, []);

  function stopPollingInterval() {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
  }

  // eslint-disable-next-line consistent-return
  async function startAsyncTask(
    asyncTaskFunction: () => Promise<IAsyncTaskResponse>,
    {
      pollingResponseTransformer = defaultPollingResponseTransformer,
    }: IAsyncTaskConfig = {}
  ) {
    try {
      setTaskStatus({
        status: AsyncTaskStatus.IN_PROGRESS,
      });
      const { uri, task_id, task_type } = await asyncTaskFunction();
      taskId.current = task_id;
      taskType.current = task_type;

      setTaskStatus({
        status: AsyncTaskStatus.IN_PROGRESS,
        errors: [],
        task_id: taskId.current,
        task_type,
      });

      startTaskStatusPoll(uri, pollingResponseTransformer);
    } catch (error) {
      setTaskStatus({
        status: AsyncTaskStatus.ERROR,
        errors: [],
      });
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        errors: error.data,
        status: error.status,
      });
    }
  }

  async function startTaskStatusPoll(
    uri: string,
    responseTransformer: PollingResponseTransformer
  ) {
    if (!pollingInterval.current) {
      pollingInterval.current = setInterval(async () => {
        try {
          if (!hasRequestInFlight.current) {
            hasRequestInFlight.current = true;

            const response = await sdk.http.get(uri);

            handleTaskStatusResponse(responseTransformer(response));
          }
        } catch (errors) {
          stopPollingInterval();
          let taskErrors = errors;
          hasRequestInFlight.current = false;

          if (errors.status === 422) {
            pushErrorFromAPI(
              {
                status: errors.status,
                errors: errors.data.errors,
              },
              {
                errorMessageCallback: (errorMessages) => {
                  taskErrors = errorMessages;
                },
              }
            );
          }

          setTaskStatus({
            status: AsyncTaskStatus.ERROR,
            task_id: taskId.current,
            task_type: taskType.current,
            errors: taskErrors,
          });
        }
      }, POLLING_INTERVAL);
    }
  }

  function handleTaskStatusResponse({
    __status__,
    errors,
    download_url,
  }: IAsyncTaskResponse) {
    hasRequestInFlight.current = false;
    switch (__status__) {
      case AsyncTaskAPIStatusCodes.IN_PROGRESS: {
        if (taskStatus.status !== AsyncTaskStatus.IN_PROGRESS) {
          setTaskStatus({
            errors: [],
            task_id: taskId.current,
            task_type: taskType.current,
            status: AsyncTaskStatus.IN_PROGRESS,
          });
        }
        break;
      }
      case AsyncTaskAPIStatusCodes.COMPLETED_SUCCESSFULLY: {
        const taskStatusObj: IAsyncTaskStatus = {
          errors: [],
          task_id: taskId.current,
          task_type: taskType.current,
          status: AsyncTaskStatus.COMPLETE,
        };
        if (download_url) {
          taskStatusObj.downloadUrl = download_url;
        }
        setTaskStatus(taskStatusObj);
        stopPollingInterval();
        break;
      }
      case AsyncTaskAPIStatusCodes.COMPLETED_UNSUCCESSFULLY: {
        let taskErrors = [];

        pushErrorFromAPI(
          {
            status: __status__,
            errors,
          },
          {
            errorMessageCallback: (errorMessages) => {
              taskErrors = errorMessages;
            },
          }
        );

        setTaskStatus({
          errors: taskErrors,
          task_id: taskId.current,
          task_type: taskType.current,
          status: AsyncTaskStatus.ERROR,
        });
        stopPollingInterval();
        break;
      }
      default: {
        setTaskStatus({
          ...taskStatus,
          status: AsyncTaskStatus.COMPLETE,
        });
        stopPollingInterval();
      }
    }
  }

  return {
    ...taskStatus,
    startAsyncTask,
  };
}
